import React, {useEffect, useRef, useState, useSyncExternalStore} from 'react';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {useAppDispatch, useAppSelector} from "../redux/hooks";
import {UserLoginData} from "../types/types";
import {accountWebLogin, clearPendingPasswordLogin} from "../redux/api/api";
import {logAction} from "../utils/appEventLogs";
import {classNames} from "primereact/utils";
import {matchPath, useNavigate} from "react-router-dom";
import {getRoleVision} from "../redux/api/apiRoles";
import {menuConfig} from "../redux/config";
import {Toast} from "primereact/toast";
import {changeActiveTab, stopAllHubConnections, stopGlobalJsSIP} from "../components/JsSip/GlobalJsSIP";
import {CHANGE_CURRENT_USER_PASSWORD_SUCCESS, CHANGE_PASSWORD_SUCCESS} from "../redux/types/types";
import {googleAuthLoginError} from "../redux/actions/actions";
import {reconnectTab, setActiveSessionTab} from "../redux/actions/actionsWebRTC";
import {LoginEnvironmentCheck} from "../components/LoginEnvironmentCheck/LoginEnvironmentCheck";
import QRCode from "qrcode.react";
import {TwoFactorCodeField} from "../components/TwoFactor/TwoFactorCodeField";
import {verifyTwoFactorLogin} from "../redux/api/apiTwoFactor";
import {CLEAR_TWO_FACTOR_CHALLENGE, ERROR_ACCOUNT_WEB_LOGIN, SET_TWO_FACTOR_CHALLENGE, SET_TWO_FACTOR_SETTINGS} from "../redux/types/types";
import {loadTwoFactorChallenge, loginQrImageSrc, readOtpauthSetup, removeTwoFactorChallenge} from "../utils/twoFactorAuth";
import {decodeQrImage} from "../utils/decodeQrImage";
import {getSessionEndedNotice, subscribeSessionEndedNotice} from "../utils/sessionEndedNotice";

export const Login = (props: any) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    let userReducer = useAppSelector(state => state.UsersReducer);
    let loading = useAppSelector(state => state.CompaniesListReducer.userLoading);
    const statusCode = JSON.parse(localStorage.getItem('callcenter:accountWebLogin') || '{}');
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as any;
    const rolesError = useAppSelector<any>(state => state.RolesReducer.error);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const pendingRedirectPath = useAppSelector(state => state.UsersReducer.pendingRedirectPath);
    const [showPassword, setShowPassword] = useState(false);
    const googleAuthError = useAppSelector(state => state.CompaniesListReducer.googleAuthError) as any;
    // @ts-ignore
    const changePasswordSuccess = useAppSelector(state => state.UsersListReducer.changePasswordSuccess);
    const changeCurrentUserPasswordSuccess = useAppSelector(state => state.UsersReducer.changeCurrentUserPasswordSuccess);
    const isLoggedOut = sessionStorage.getItem('isLoggedOut');
    const enrollmentQr = useAppSelector(state => state.TwoFactorReducer.enrollmentQr);
    const awaitingAuthCode = useAppSelector(state => state.TwoFactorReducer.awaitingAuthCode);
    const challengeToken = useAppSelector(state => state.TwoFactorReducer.challengeToken);
    const challengeUserName = useAppSelector(state => state.TwoFactorReducer.challengeUserName);
    const twoFactorError = useAppSelector(state => state.TwoFactorReducer.error);
    const twoFactorLoading = useAppSelector(state => state.TwoFactorReducer.loading);
    const authNotice = useAppSelector(state => state.TwoFactorReducer.authNotice);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [codeFieldKey, setCodeFieldKey] = useState(0);
    const [manualKey, setManualKey] = useState<string | null>(null);
    const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
    const [showManualKey, setShowManualKey] = useState(false);
    const lastSubmittedCode = useRef('');
    const codeErrorWasShown = useRef(false);
    const sessionEndedNotice = useSyncExternalStore(subscribeSessionEndedNotice, getSessionEndedNotice, getSessionEndedNotice);


    useEffect(() => {
        if (!sessionEndedNotice) {
            return;
        }
        toast.current?.show({
            severity: 'warn',
            summary: sessionEndedNotice,
            detail: '',
            sticky: true,
            closable: false
        });
    }, [sessionEndedNotice]);

    const authNoticeToast = useRef<any>(null);

    useEffect(() => {
        if (!authNotice || authNoticeToast.current) {
            return;
        }
        const [summary, ...rest] = authNotice.split('\n');
        const message = {
            severity: 'warn' as const,
            summary,
            detail: rest.join('\n'),
            sticky: true,
            closable: true,
            className: 'login-auth-notice-toast'
        };
        authNoticeToast.current = message;
        toast.current?.show(message);
    }, [authNotice]);

    useEffect(() => {
        if (changePasswordSuccess) {
            const message = (changePasswordSuccess as any)?.message || 'Лист відновлення паролю відправлений';
            toast?.current?.show({severity: 'success', summary: message, detail: '', life: 3000});
            dispatch({type: CHANGE_PASSWORD_SUCCESS, payload: false});
        }
    }, [changePasswordSuccess]);

    useEffect(() => {
        if (!!changeCurrentUserPasswordSuccess) {
            const message = (changeCurrentUserPasswordSuccess as any)?.message || 'Лист відновлення паролю відправлений';
            toast?.current?.show({severity: 'success', summary: message, detail: '', life: 3000});
            setTimeout(() => {
                dispatch({type: CHANGE_CURRENT_USER_PASSWORD_SUCCESS, payload: false});
            }, 3000);
        }
    }, [changeCurrentUserPasswordSuccess]);

    useEffect(() => {
        stopAllHubConnections();
        stopGlobalJsSIP();
    }, []);

    useEffect(() => {
        if (!!googleAuthError) {
            let message = googleAuthError || 'Щось пішло не так.';
            toast?.current?.show({severity: 'error', summary: message, detail: '', life: 3000});
            setTimeout(() => {
                dispatch(googleAuthLoginError(false));
            }, 1000);
        }
    }, [googleAuthError]);

    useEffect(() => {
        if (rolesError) {
            toast?.current?.show({severity: 'error', summary: 'Щось пішло не так.', detail: '', life: 3000});
        }
    }, [rolesError]);

    useEffect(() => {
        if (!!googleAuthError) {
            let message = googleAuthError || 'Щось пішло не так.';
            toast?.current?.show({severity: 'error', summary: message, detail: '', life: 3000});
        }
    }, [googleAuthError]);

    const [userLogin, setUserLogin] = useState<UserLoginData>({
        userName: '',
        password: ''
    });
    const [loginErrors, setLoginErrors] = useState<{ userName?: string; password?: string }>({});

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        if (statusCode === 200) {
            if (!rolesVision && rolesError === false && jwtToken?.jwtToken && !isLoggedOut) {
                dispatch(getRoleVision(jwtToken?.jwtToken));
            }

            if (rolesVision && jwtToken?.jwtToken && !isLoggedOut) {

                const filteredMenuConfigWithRole = menuConfig.filter((item) =>
                    rolesVision.hasOwnProperty(item.name)
                );
                if (filteredMenuConfigWithRole.length) {
                    dispatch({type: CLEAR_TWO_FACTOR_CHALLENGE});
                    const order = rolesVision ? Object.keys(rolesVision) : [];

                    filteredMenuConfigWithRole?.sort((a, b) => {
                        const indexA = order?.indexOf(a.name);
                        const indexB = order?.indexOf(b.name);
                        return indexA - indexB;
                    });
                    if (pendingRedirectPath !== null && matchPath("/call_details/:id", pendingRedirectPath)) {
                        navigate(`${pendingRedirectPath}`);
                        // window.location.reload();
                        changeActiveTab();
                        dispatch(setActiveSessionTab(true));
                        dispatch(reconnectTab(true));
                    }
                    else if (filteredMenuConfigWithRole[0].name.toLowerCase() === 'dialer') {
                        navigate(`/`);
                        changeActiveTab();
                        dispatch(setActiveSessionTab(true));
                        dispatch(reconnectTab(true));
                        // window.location.reload();
                    } else {
                        navigate(`/${filteredMenuConfigWithRole[0].name.toLowerCase()}`);
                        // window.location.reload();
                        changeActiveTab();
                        dispatch(setActiveSessionTab(true));
                        dispatch(reconnectTab(true));
                    }
                }
            }
        }
    }, [rolesVision, statusCode, rolesError, jwtToken?.jwtToken, isLoggedOut, pendingRedirectPath]);

    const updateUserLoginData = (event: any) => {
        const value = event.target.value;
        const fieldName = event.target.name;
        let userLoginCopy = {...userLogin};
        // @ts-ignore
        userLoginCopy[fieldName] = value;
        setUserLogin(userLoginCopy);
        if (loginErrors[fieldName as keyof typeof loginErrors]) {
            setLoginErrors((prev) => ({ ...prev, [fieldName]: undefined }));
        }
    };

    const validateLogin = (): boolean => {
        const errors: { userName?: string; password?: string } = {};
        const trimmedUserName = (userLogin.userName || '').trim();
        const trimmedPassword = (userLogin.password || '').trim();
        if (!trimmedUserName) {
            errors.userName = 'Введіть логін';
        }
        if (!trimmedPassword) {
            errors.password = 'Введіть пароль';
        }
        setLoginErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        if (challengeToken) {
            return;
        }
        if (sessionStorage.getItem('isLoggedOut') === 'true') {
            removeTwoFactorChallenge();
            return;
        }
        const savedChallenge = loadTwoFactorChallenge();
        if (!savedChallenge || savedChallenge.challengeToken === 'after-login' || savedChallenge.challengeToken === 'preview-challenge') {
            removeTwoFactorChallenge();
            return;
        }
        dispatch({type: SET_TWO_FACTOR_CHALLENGE, payload: savedChallenge});
    }, [challengeToken]);

    useEffect(() => {
        setTwoFactorCode('');
        lastSubmittedCode.current = '';
    }, [challengeToken, enrollmentQr, awaitingAuthCode]);

    useEffect(() => {
        if (!enrollmentQr) {
            setManualKey(null);
            setOtpauthUrl(null);
            setShowManualKey(false);
            return;
        }
        let cancelled = false;
        decodeQrImage(loginQrImageSrc(enrollmentQr)).then((text) => {
            if (cancelled) {
                return;
            }
            const setup = text ? readOtpauthSetup(text) : null;
            setOtpauthUrl(setup?.otpauthUrl || null);
            setManualKey(setup?.manualKey || null);
            setShowManualKey(false);
        });
        return () => {
            cancelled = true;
        };
    }, [enrollmentQr]);

    const enrollmentCodeError = Boolean((enrollmentQr || awaitingAuthCode) && (twoFactorError || userReducer.message));

    useEffect(() => {
        if (!enrollmentCodeError) {
            codeErrorWasShown.current = false;
            return;
        }
        if (codeErrorWasShown.current) {
            return;
        }
        codeErrorWasShown.current = true;
        setTwoFactorCode('');
        lastSubmittedCode.current = '';
        setCodeFieldKey((key) => key + 1);
    }, [enrollmentCodeError]);

    const clearCodeError = () => {
        if (twoFactorError) {
            dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {error: null}});
        }
        if (userReducer.message) {
            dispatch({type: ERROR_ACCOUNT_WEB_LOGIN, payload: {"statusCode": null, "title": null, "message": null, "data": null}});
        }
    };

    const handleLogin = () => {
        if (!validateLogin()) {
            return;
        }
        logAction('login_submit', {userName: (userLogin.userName || '').trim()});
        dispatch(accountWebLogin(userLogin));
    };

    const submitEnrollmentCode = (value: string, force = false) => {
        const code = value.trim();
        if ((!enrollmentQr && !awaitingAuthCode) || code.length < 6 || loading || twoFactorLoading) {
            return;
        }
        if (!force && lastSubmittedCode.current === code) {
            return;
        }
        lastSubmittedCode.current = code;
        logAction('login_2fa_submit', {userName: userLogin.userName, setup: true});
        dispatch(accountWebLogin({...userLogin, code2FA: code}));
    };

    const copyManualKey = async () => {
        if (!manualKey) {
            return;
        }
        try {
            await navigator.clipboard.writeText(manualKey);
        } catch {
            const area = document.createElement('textarea');
            area.value = manualKey;
            area.setAttribute('readonly', '');
            area.style.position = 'fixed';
            area.style.left = '-9999px';
            document.body.appendChild(area);
            area.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(area);
            if (!copied) {
                return;
            }
        }
        toast.current?.show({severity: 'success', summary: 'Ключ успішно скопійовано', detail: '', life: 3000});
    };

    const cancelEnrollment = () => {
        dispatch({type: CLEAR_TWO_FACTOR_CHALLENGE});
        dispatch({type: ERROR_ACCOUNT_WEB_LOGIN, payload: {"statusCode": null, "title": null, "message": null, "data": null}});
        setTwoFactorCode('');
        lastSubmittedCode.current = '';
    };

    const submitTwoFactorCode = (value: string, force = false) => {
        const code = value.trim();
        if (!challengeToken || code.length < 6 || twoFactorLoading) {
            return;
        }
        if (!force && lastSubmittedCode.current === code) {
            return;
        }
        lastSubmittedCode.current = code;
        if (code === '111111') {
            lastSubmittedCode.current = '';
            setTwoFactorCode('');
            setCodeFieldKey((key) => key + 1);
        }
        logAction('login_2fa_submit', {userName: challengeUserName || userLogin.userName});
        dispatch(verifyTwoFactorLogin(challengeToken, code));
    };

    const cancelTwoFactor = () => {
        clearPendingPasswordLogin();
        removeTwoFactorChallenge();
        dispatch({type: CLEAR_TWO_FACTOR_CHALLENGE});
        setTwoFactorCode('');
        lastSubmittedCode.current = '';
    };

    // const handleGoogleRegister = () => {
    //     const timestamp = new Date().getTime();
    //     window.location.href = `${API_URL}AccountWeb/google-register?timestamp=${timestamp}`;
    // };

    return (
        <div className="login-body">
            <Toast
                position="bottom-right"
                style={{zIndex: 10000}}
                ref={toast}
                onRemove={(message) => {
                    if (message?.className !== 'login-auth-notice-toast') {
                        return;
                    }
                    authNoticeToast.current = null;
                    dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {authNotice: null}});
                }}
            />
            <div className="login-image">
                <img src={`assets/layout/images/${props.colorScheme === 'light' ? '3' : '3'}.jpeg`} alt="atlantis"/>
                {/*<img src={`assets/layout/images/pages/login-onlight.png`} alt="atlantis"/>*/}
                {/*<img src={`assets/layout/images/spr.jpeg`} alt="atlantis"/>*/}
            </div>
                <div className={classNames('login-panel', 'p-fluid', 'login-panel--env-check')}>
                <div className="flex flex-column">
                    <h3>Вхід</h3>
                    {enrollmentQr || awaitingAuthCode ? (
                        <div className="form-container login-enrollment-form">
                            <p className="mt-0 mb-3">{enrollmentQr ? 'Відскануйте QR у Google Authenticator.' : 'Введіть код із додатка'}</p>
                            {enrollmentQr ? <div className="login-enrollment-qr">
                                <div className="bg-white border-round login-enrollment-qr__frame">
                                    {otpauthUrl ? (
                                        <QRCode value={otpauthUrl} size={220} includeMargin bgColor="#ffffff" fgColor="#000000" level="M"/>
                                    ) : (
                                        <img src={loginQrImageSrc(enrollmentQr)} alt="Google Authenticator"/>
                                    )}
                                </div>
                            </div> : null}
                            {enrollmentQr && manualKey ? (
                                <div className="login-enrollment-key">
                                    {!showManualKey ? (
                                        <button type="button" className="p-link text-sm text-primary cursor-pointer border-none bg-transparent px-0" onClick={() => setShowManualKey(true)}>Підключення вручну</button>
                                    ) : (
                                        <>
                                            <label htmlFor="login-enrollment-key">Ключ аутентифікації</label>
                                            <div className="login-enrollment-key__row">
                                                <InputText id="login-enrollment-key" value="***************************" disabled readOnly/>
                                                <Button type="button" icon="pi pi-copy" className="login-enrollment-key__copy" aria-label="Копіювати ключ" onClick={copyManualKey}/>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : null}
                            <TwoFactorCodeField
                                key={codeFieldKey}
                                id="login-enrollment-code"
                                value={twoFactorCode}
                                autoFocus
                                invalid={Boolean(twoFactorError || userReducer.message)}
                                onChange={(value) => {
                                    setTwoFactorCode(value);
                                    clearCodeError();
                                    if (/^\d{6}$/.test(value)) {
                                        submitEnrollmentCode(value);
                                    }
                                }}
                                onSubmit={() => submitEnrollmentCode(twoFactorCode, true)}
                            />
                            {(twoFactorError || userReducer.message) && <small className="p-error block mt-2">{twoFactorError || userReducer.message}</small>}
                            <button type="button" className="p-link flex mb-2 text-sm mt-4 text-primary cursor-pointer border-none bg-transparent px-0" onClick={cancelEnrollment}>Назад до пароля</button>
                        </div>
                    ) : challengeToken ? (
                        <div className="form-container">
                            <p className="mt-0 mb-3">Введіть код аутентифікації</p>
                            <TwoFactorCodeField
                                key={codeFieldKey}
                                id="login-two-factor-code"
                                value={twoFactorCode}
                                autoFocus
                                invalid={Boolean(twoFactorError)}
                                onChange={(value) => {
                                    setTwoFactorCode(value);
                                    clearCodeError();
                                    if (/^\d{6}$/.test(value)) {
                                        submitTwoFactorCode(value);
                                    }
                                }}
                                onSubmit={() => submitTwoFactorCode(twoFactorCode, true)}
                            />
                            {twoFactorError && <small className="p-error block mt-2">{twoFactorError}</small>}
                            {/*<div className="button-container">*/}
                            {/*    <Button onClick={() => submitTwoFactorCode(twoFactorCode, true)} loading={twoFactorLoading} disabled={twoFactorCode.trim().length < 6} className="mt-3 mb-3" type="button" label="Підтвердити"/>*/}
                            {/*</div>*/}
                            <button type="button" className="p-link flex mb-2 text-sm mt-4 text-primary cursor-pointer border-none bg-transparent px-0" onClick={cancelTwoFactor}>Назад до пароля</button>
                        </div>
                    ) : (
                    <><form className="form-container">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-user"></i>
                            <InputText value={userLogin.userName} name="userName"
                                       onChange={(e) => updateUserLoginData(e)} type="text" placeholder="Логін"
                                       autoComplete="username"
                                       className={classNames('w-full', {'p-invalid': loginErrors.userName || (userReducer.statusCode && userReducer.statusCode !== 200)})}/>
                        </span>
                        {loginErrors.userName && <small className="p-error block mt-1">{loginErrors.userName}</small>}
                        <span className="p-input-icon-left w-full mt-2">
                            <i className="pi pi-key"></i>
                            <InputText value={userLogin.password} name="password"
                                       onChange={(e) => updateUserLoginData(e)}
                                       type={showPassword ? 'text' : 'password'} placeholder="Пароль"
                                       autoComplete="current-password"
                                       className={classNames('pr-6 w-full', {'p-invalid': loginErrors.password || (userReducer.statusCode && userReducer.statusCode !== 200)})}/>
                            <i className={showPassword ? 'pi pi-eye-slash cursor-pointer text-black show-pass text-primary' : 'pi pi-eye cursor-pointer show-pass text-primary'}
                               onClick={togglePasswordVisibility}></i>
                        </span>
                        {loginErrors.password && <small className="p-error block mt-1">{loginErrors.password}</small>}
                        {userReducer.message &&
                            <small id="username-help" className="p-error">{userReducer.message}</small>}
                    </form>
                    <div className="button-container">
                        <Button onClick={handleLogin} loading={loading} className="mt-3 mb-3" type="button" label="Увійти"></Button>
                    </div>
                    <div className="button-container">
                        <a target="_blank" onClick={() => navigate('/auth/forgotpassword')}
                           className="flex mb-2 text-sm text-primary cursor-pointer">Забули свій пароль ?</a>
                    </div>
                    </>)}
                </div>

                <LoginEnvironmentCheck />

                <div className="login-footer flex align-items-center">
                    <span>Copyright {new Date().getFullYear()}</span>
                </div>
            </div>
        </div>
    );
};
